import { doodBlockConfig, propertyValue } from "./dood-block-config";
import { cartPagePresetBlockBackgroundColorProperty } from "./cart-page-preset-block-background.util";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";
import type { LoyaltyPreviewOfferedProduct } from "./preview-scenarios/loyalty-preview.shared";

/** Reward row shown by the Zerosix (06) asset list in CB2 preview. */
export type ZerosixAssetListPreviewAsset = {
  id: string;
  type?: "voucher" | "reward";
  url: string;
  name: string;
  value?: number;
  unit: string;
  code: string;
  external_ids: readonly string[];
  is_usable?: boolean;
  points_required?: number;
  offeredProducts?: readonly LoyaltyPreviewOfferedProduct[];
};

/** Serializable preview VM for `ZerosixAssetListBlockComponent`. */
export type ZerosixAssetListBlockScenarioContent = {
  loading: boolean;
  memberCard: {
    terms_accepted: boolean;
    points_balance: number;
    current_level: string;
    card_url: string;
    available_vouchers: ZerosixAssetListPreviewAsset[];
    rewards: ZerosixAssetListPreviewAsset[];
  } | null;
};

export const ZEROSIXASSETLISTBLOCK_LEGACY_FIELD_ORDER = [
  "heading",
  "subheading",
  { type: "separator" },
  "width",
  "items_to_show_card_carousel",
  { type: "heading", value: "Couleurs" },
  "heading_color",
  "text_color",
  "asset_name_color",
  "asset_details_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  zerosixAssetListBlockDescriptor: _generatedZerosixAssetListBlockDescriptor,
  zerosixAssetListBlockBlocDefinition,
  zerosixAssetListBlockDashboardLayout,
  zerosixAssetListBlockConfig,
  zerosixAssetListBlockResolveConfig,
  zerosixAssetListBlockProperties,
  zerosixAssetListBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Zerosix - Asset list",
    componentType: "ZerosixAssetListBlockComponent",
    category: "zerosix",
    description: "Zerosix - Asset list.",
    keywords: [],
    sharedFields: {},
  },

  () => ({
    background_color: cartPagePresetBlockBackgroundColorProperty(),
    heading: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.zerosix-asset-list.heading.label",
      },
    }),
    subheading: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.zerosix-asset-list.subheading.label",
      },
    }),
    width: propertyValue({
      type: "select",
      value: "centered",
      options: {
        label: "pages.blocks.zerosix-asset-list.width.label",
        choices: [
          {
            value: "centered",
            label: "pages.blocks.zerosix-asset-list.width.options.centered",
          },
          {
            value: "full",
            label: "pages.blocks.zerosix-asset-list.width.options.full",
          },
        ],
      },
    }),
    items_to_show_card_carousel: propertyValue({
      type: "number",
      value: 4,
      options: {
        label:
          "pages.blocks.zerosix-asset-list.items-to-show-card-carousel.label",
        help: "pages.blocks.zerosix-asset-list.items-to-show-card-carousel.help",
        placeholder: "4",
      },
    }),
    heading_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: {
        label: "pages.blocks.zerosix-asset-list.heading-color.label",
      },
    }),
    text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label: "pages.blocks.zerosix-asset-list.text-color.label",
      },
    }),
    asset_name_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: {
        label: "pages.blocks.zerosix-asset-list.asset-name-color.label",
      },
    }),
    asset_details_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: {
        label: "pages.blocks.zerosix-asset-list.asset-details-color.label",
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.zerosix-asset-list.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.cart-page.fieldset.appearance.label",
            fields: ["background_color"],
          },
          {
            legend: "pages.blocks.zerosix-asset-list.fieldset.header.label",
            fields: ["heading", "subheading", "heading_color", "text_color"],
          },
          {
            legend: "pages.blocks.zerosix-asset-list.fieldset.layout.label",
            fields: ["width", "items_to_show_card_carousel"],
          },
          {
            legend: "pages.blocks.zerosix-asset-list.fieldset.rewards.label",
            fields: ["asset_name_color", "asset_details_color"],
          },
        ],
      },
    ],
  }),
);

export function zerosixAssetListBlockDescriptor(): IBlockDescriptor;
export function zerosixAssetListBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function zerosixAssetListBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedZerosixAssetListBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedZerosixAssetListBlockDescriptor,
    zerosixAssetListBlockProperties,
    fieldOrder,
  );
}

export {
  zerosixAssetListBlockBlocDefinition,
  zerosixAssetListBlockDashboardLayout,
  zerosixAssetListBlockProperties,
  zerosixAssetListBlockConfig,
  zerosixAssetListBlockResolveConfig,
  zerosixAssetListBlockDefinitionSetupScenarios,
};

export type ZerosixAssetListBlockConfig = typeof zerosixAssetListBlockConfig;
